<template>
  <a-button
    class="mb-2 ml-1"
    type="primary"
    style="width: min-content"
    @click="handleBackButton"
  >
    <template #icon>
      <ArrowLeftOutlined />
    </template>
    Back</a-button
  >
  <a-table
    :data-source="validationGroupResult"
    :columns="validationGroupResultColumn"
    :scroll="{
      y: '55vh',
      x: true,
    }"
    :loading="isLoading"
    :pagination="{
      pageSize: 10,
      showSizeChanger: true,
      showTotal: (total) => `Total ${total} items`,
    }"
    row-key="id"
    class="group-result-table"
  >
    <template #bodyCell="{ record, column }">
      <template v-if="column.dataIndex === 'fileName'">
        <a-typography-text strong>
          {{ record.video_name }}
        </a-typography-text>
      </template>

      <template v-else-if="column.dataIndex === 'accuracy'">
        <a-tag>
          {{ Number(record.results?.accuracy).toFixed(3) }}
        </a-tag>
      </template>

      <template v-else-if="column.dataIndex === 'balancedAccuracy'">
        <a-tag color="green">
          {{ Number(record.results?.balanced_accuracy).toFixed(3) }}
        </a-tag>
      </template>

      <template v-else-if="column.dataIndex === 'image'">
        <a-image
          class="d-flex justify-content-center align-items-center"
          :src="record.image"
          :width="350"
          :height="154"
        />
      </template>

      <template v-else-if="column.dataIndex === 'video'">
        <img
          v-if="record?.videoThumbnail"
          :src="record.videoThumbnail"
          width="140"
          height="124"
          style="cursor: pointer"
          @click="openVideoPlayerModal(record)"
        />
      </template>
    </template>

    <template #emptyText>
      <a-empty description="No validation group result found" />
    </template>
  </a-table>
  <a-modal
    v-model:visible="displayVideoPlayer"
    :title="videoFileName"
    :footer="null"
    :mask-closable="false"
    centered
    width="50vw"
    :body-style="{
      display: 'flex',
      flexDirection: 'column',
    }"
  >
    <video
      ref="videoPlayer"
      class="w-100 mv-3 rounded"
      muted
      controls
      style="
        max-height: 45vh;
        width: auto;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        margin-bottom: 1em;
      "
      :src="videoUrl"
      @error="handleVideoError"
    />
  </a-modal>
</template>
<script setup>
import { ArrowLeftOutlined } from '@ant-design/icons-vue';
import S3Service from 'src/services/s3';
import TelemetryService from 'src/services/telemetry';
import { getThumbnailUrl, validationThumbnail } from 'src/utils/thumbnail';
import { inject, onMounted, ref } from 'vue';
import { validationGroupResultColumn } from './config';
// Props
const props = defineProps({
  runId: {
    type: Number,
    default: null,
  },

  taskName: {
    type: String,
    default: '',
  },

  organization: {
    type: String,
    default: '',
  },
});

// Emit
const emit = defineEmits(['handleBack']);

// injection
const toast = inject('toast');

// Reactive state
const isLoading = ref(false);
const videoFileName = ref(null);
const videoUrl = ref(null);
const displayVideoPlayer = ref(false);
const validationGroupResult = ref([]);

// Methods
onMounted(() => {
  getValidationGroupResult();
});

const handleVideoError = (e) => {
  toast.error('The video format is not supported!');
};

const openVideoPlayerModal = (record) => {
  videoUrl.value = record.videoUrl;
  videoFileName.value = record.video_name;
  displayVideoPlayer.value = true;
};

const getValidationGroupResult = async () => {
  try {
    isLoading.value = true;
    const [error, data] = await TelemetryService.getValdiationRunResult(
      props.runId
    );
    if (error) return toast.error('Failed to retrieve result.');
    validationGroupResult.value = data;
    await getPresignedUrls(validationGroupResult.value);
  } catch (error) {
    console.error(error);
    isLoading.value = false;
  } finally {
    isLoading.value = false;
  }
};

const getPresignedUrls = async (list) => {
  for (const item of list) {
    let [error, data] = await S3Service.generatePresignedUrl(
      `${props.organization}-telemetry`,
      `${item.validation_results_path}/steps_comparison.png`
    );
    if (!error) {
      item.image = data.presigned_url;
    }

    [error, data] = await S3Service.generatePresignedUrl(
      `${props.organization}-telemetry`,
      `${item.validation_results_path}/steps_comparison.mp4`
    );
    if (!error) {
      let url = validationThumbnail(
        { fileName: item.video_name, Task: { taskName: props.taskName } },
        props.organization
      );
      item.videoThumbnail = getThumbnailUrl(url);
      item.videoUrl = data.presigned_url;
    }
  }
};

const handleBackButton = () => {
  emit('handleBack');
};
</script>

<script>
export default {
  name: 'ValidationGroupResult',
  components: {
    ArrowLeftOutlined,
  },
};
</script>
